import { AppContextState } from '../components/app/AppProvider.component';
import {
  QuizAttemptDetailsDto,
  QuizSessionDto,
  RecommendationAttemptDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RecommendationItemDto,
  RemediationActivityTypeDto
} from '../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  AssessmentTypeDto,
  EolsAssessmentDto
} from '../apis/eols-assessment-service/eols-assessment-service.dtos';
import { History } from 'history';
import { RoutePath } from '../components/app/app.constants';

export type NavigateToContentProps = AppContextState & {
  recommendation: RecommendationDto;
  recommendationAttempt: RecommendationAttemptDto;
  assessment: EolsAssessmentDto;
  quizSession: QuizSessionDto;
  history: History;
  ref: RoutePath;
}
export type GoalBreadcrumb = {
  text: string;
  path: string[];
  pathDisplay: string;
  pathFull: string[];
  pathFullDisplay: string;
}

export enum RemediationGoalType {
  TAXON = 'TAXON',
  CONTENT_TYPE = 'CONTENT_TYPE',
}

export const ActivityTypeConfigMap: Record<RemediationActivityTypeDto, {
  title: string;
  iconName: string;
  iconPrefix: string;
  goalType: RemediationGoalType;
  sortOrder: number;
}> = {
  [RemediationActivityTypeDto.REVIEW_BY_TAXON]: {
    title: 'Review',
    iconName: 'books',
    iconPrefix: 'gizmo',
    goalType: RemediationGoalType.TAXON,
    sortOrder: 1,
  },
  [RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE]: {
    title: 'Apply',
    iconName: 'laptop',
    iconPrefix: 'gizmo',
    goalType: RemediationGoalType.CONTENT_TYPE,
    sortOrder: 2,
  },
  [RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE]: {
    title: 'Assess',
    iconName: 'approve-document',
    iconPrefix: 'gizmo',
    goalType: RemediationGoalType.CONTENT_TYPE,
    sortOrder: 3,
  },
};

export const RemediationContentTypeConfigMap: Record<RecommendationContentTypeDto, {
  displayName: string;
  titlePlural: string;
  titleSingular: string;
  activityType: RemediationActivityTypeDto;
  iconName: string;
  iconPrefix: string;
  sortOrder: number;
  isScored: boolean;
  assessmentType: AssessmentTypeDto;
}> = {
  [RecommendationContentTypeDto.EBOOK_READING]: {
    displayName: 'eBook excerpt',
    titlePlural: 'Readings',
    titleSingular: 'Reading',
    activityType: RemediationActivityTypeDto.REVIEW_BY_TAXON,
    iconName: 'open-book',
    iconPrefix: 'gizmo',
    sortOrder: 0,
    isScored: false,
    assessmentType: AssessmentTypeDto.RAAS_EBOOK_READING
  },
  [RecommendationContentTypeDto.OSMOSIS_VIDEO]: {
    displayName: 'Osmosis video',
    titlePlural: 'Videos',
    titleSingular: 'Video',
    activityType: RemediationActivityTypeDto.REVIEW_BY_TAXON,
    iconName: 'play-video',
    iconPrefix: 'gizmo',
    sortOrder: 1,
    isScored: false,
    assessmentType: AssessmentTypeDto.RAAS_OSMOSIS_VIDEO

  },
  [RecommendationContentTypeDto.TOPIC_OVERVIEW]: {
    displayName: 'Topic overview',
    titlePlural: 'Topic Overviews',
    titleSingular: 'Topic Overview',
    activityType: RemediationActivityTypeDto.REVIEW_BY_TAXON,
    iconName: 'list',
    iconPrefix: 'gizmo',
    sortOrder: 2,
    isScored: false,
    assessmentType: AssessmentTypeDto.RAAS_EBOOK_READING // TODO(FLOR-3766): update when backend supports a dedicated assessment type for TOPIC_OVERVIEW
  },
  [RecommendationContentTypeDto.HESI_CASE_STUDY]: {
    displayName: 'Case study',
    titlePlural: 'Case Studies',
    titleSingular: 'Case Study',
    activityType: RemediationActivityTypeDto.APPLY_BY_CONTENT_TYPE,
    iconName: 'laptop',
    iconPrefix: 'gizmo',
    sortOrder: 3,
    isScored: true,
    assessmentType: AssessmentTypeDto.RAAS_CASE_STUDY
  },
  [RecommendationContentTypeDto.RAAS_QUIZ]: {
    displayName: 'Quiz',
    titlePlural: 'Quiz Questions',
    titleSingular: 'Quiz Question',
    activityType: RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE,
    iconName: 'approve-document',
    iconPrefix: 'gizmo',
    sortOrder: 4,
    isScored: false,
    assessmentType: AssessmentTypeDto.RAAS_QUIZ
  },
};

export enum RemediationStatusCountLabel {
  COMPLETED = 'completed',
  CORRECT = 'correct',
}

export type StatusMapConfig = {
  recommendations: RecommendationDto[];
  recommendationItems: RecommendationItemDto[];
  recommendationAttempts: RecommendationAttemptDto[];
  quizAttemptDetails: QuizAttemptDetailsDto;
};

export type BaseRemediationAppLinkOutBody = {
  ref: RoutePath;
  raasAssessmentId: number;
  recommendationId: number;
  recommendationAttemptId: number;
  eolsUserId: number;
}

export type CaseStudyAppLinkOutBody = BaseRemediationAppLinkOutBody & {
  assessmentVtwId: string;
  instanceId: string;
}

export type AssessmentPlayerAppLinkOutBody = BaseRemediationAppLinkOutBody & {
  assessmentId: number;
  quizSessionId: number;
  getNextQuestionAPI: string;
  submitQuestionAPI: string;
  assessmentPlayerConfig: {
    navigationType: string;
    strictStudyMode: boolean;
    hideSubmissionConfirmationPage: boolean;
    hideMultipleChoicePrefixes: boolean;
  };
  integrationMetadata: {
    name: string;
  };
}
